import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FLOW_1_DEFAULT_AGENCY_CODE } from 'src/constants'
import { Paths, SAGA_GUARDS } from 'src/navigation'
import { shoot } from 'src/redux/actions'
import {
  select_evaluate_premium,
  select_history_item,
  select_loading,
  select_payment,
  select_product,
  select_quote,
} from 'src/redux/store'
import {
  agency_product_t,
  AxeptaWindow,
  document_t,
  InputSelectOption,
} from 'src/types'
import { get_language } from 'src/utils'
import { getGroupInitialValues, getMainInsuredInitialValues } from './handlers/form-initial-values'
import { useScrollToErrorGroup } from './handlers/scroll-to-error.hook'
import { useValidationSchemasGroup } from './handlers/validation-schema-group.hook'
import { FormikMainInsured } from 'src/types/formik'
import { FormikMainInsuredGroup } from 'src/types/formik/group.types'
import { submit_captcha } from 'src/widgets/recaptcha/recaptcha'

export const useDataEntryGroup = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const daysOptions: InputSelectOption[] = [
    {
      id: 'daily',
      value: t('data_entry_step_1.coverage_daily'),
    },
    {
      id: 'weekly_7',
      value: t('data_entry_step_1.coverage_weekly'),
    },
    {
      id: 'monthly_30',
      value: t('data_entry_step_1.coverage_monthly'),
    },
    {
      id: 'quarterly_90',
      value: t('data_entry_step_1.coverage_quarterly'),
    },
  ]

  const dailyOptions: InputSelectOption[] = [
    {
      id: 'daily_1',
      value: '1',
    },
    {
      id: 'daily_2',
      value: '2',
    },
    {
      id: 'daily_3',
      value: '3',
    },
    {
      id: 'daily_4',
      value: '4',
    },
  ]

  /**
   * useSelector
   */
  const agency: agency_product_t = useSelector(select_history_item('history_agency'))
  const historyCurrentStep: number = useSelector(select_history_item('history_current_step'))
  const historyIsGroup: string = useSelector(select_history_item('history_is_group'))
  const historyGroup = useSelector(select_history_item('history_group'))
  const historyProducts = useSelector(select_history_item('history_products'))
  const historyDocuments: document_t[] = useSelector(select_history_item('history_documents'))
  const evaluatedPremium = useSelector(select_evaluate_premium)
  const quote = useSelector(select_quote)
  const product = useSelector(select_product);
  const payment = useSelector(select_payment);
  const loadingPayment = useSelector(select_loading(SAGA_GUARDS.PAYMENT))
  const loadingPremium = useSelector(select_loading(SAGA_GUARDS.EVALUATE_PREMIUM))

  /**
   * useState
   */
  const [language] = useState(get_language().toLocaleLowerCase())
  const [currentStep, setCurrentStep] = useState(Number(historyCurrentStep))
  const [isGroup, setIsGroup] = useState(historyIsGroup && historyIsGroup === "true" ? true : false)
  const [updatePremium, setUpdatePremium] = useState(false)
  const [scrollEvaluated, setScrollEvaluated] = useState(true)
  const [isDisabled, setIsDisabled] = useState(true)
  const [discountError, setDiscountError] = useState('')

  const insuredLimit = product?.configuration?.insuredLimit || 7

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    setIsGroup(true)
    dispatch(shoot.reduce_history_item("history_is_group", "true"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (currentStep !== 0) {
      window?.scrollTo(0, 0)
    }
    if (evaluatedPremium && scrollEvaluated && currentStep === 1) {
      setScrollEvaluated(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluatedPremium, scrollEvaluated, currentStep])

  useEffect(() => {
    if (
      payment &&
      payment?.paymentId &&
      (agency?.isOriginalPayment === undefined || agency?.isOriginalPayment)
    ) {
      trackPurchase()
      const axeptaWindow: AxeptaWindow = window
      axeptaWindow?.callAxeptaClient?.()?.proceedToPayment?.(payment?.paymentId)
      dispatch(shoot.reduce_payment())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment])

  useEffect(() => {
    if (historyIsGroup === "false") {
      navigate(Paths.DataEntry)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyIsGroup])

  useEffect(() => {
    if (currentStep === 0) {
      dispatch(shoot.reduce_quote())
      dispatch(shoot.reduce_evaluate_premium())
      setIsGroup(false);
      dispatch(shoot.reduce_history_item("history_is_group", "false"));
    } else if (currentStep === 1) {
      formikMainInsured['insured_list'] = []
      setValues({ ...values })
      saveFormikMainInsuredGroup({ ...values });
      dispatch(shoot.reduce_history_item("history_group", { ...values }));
    } else if (currentStep === 2) {
      const updatedValues = { ...values, group: values.originGroup };
      saveFormikMainInsuredGroup(updatedValues);
      dispatch(shoot.reduce_history_item("history_group", updatedValues));
    }
    dispatch(shoot.reduce_error())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Functions
   */
  const trackPurchase = () => {
    const googleWindow: any = window
    googleWindow.dataLayer = googleWindow?.dataLayer || []
    googleWindow.dataLayer.push({ ecommerce: null }) // Clear the previous ecommerce object.
    const ecommerce = {
      value: +(quote?.quoteValue || 0),
      currency: 'EUR',
      items: [
        {
          item_category: formikMainInsured.coverage_type_radios === '1' ? 'Base' : 'Plus',
        },
      ],
    }
    dispatch(shoot.reduce_history_item('history_ecommerce', ecommerce))
    googleWindow.dataLayer.push({ event: 'begin_checkout', ecommerce })
  }

  const goBack = () => {
    dispatch(shoot.reduce_quote())
    dispatch(shoot.reduce_evaluate_premium())
    if (currentStep > 0) {
      let state = { ...values }
      // state['discount'] = ''
      setValues(state)
      setCurrentStep((x) => Math.max(0, x - 1))
      dispatch(
        shoot.reduce_history_item('history_current_step', (currentStep - 1).toString())
      )
    } else {
      if (agency?.isOriginalPayment === undefined || agency?.isOriginalPayment) {
        dispatch(shoot.reduce_history_item('history_current_step', '0'))
        navigate(Paths.Landing + (agency.id !== FLOW_1_DEFAULT_AGENCY_CODE ? agency.id : ''))
      } else {
        dispatch(shoot.reduce_history_item('history_current_step', '1'))
        navigate(Paths.DataEntry)
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Formik
   */
  const [formikMainInsured] = useState<FormikMainInsured>(
    historyProducts ? JSON.parse(historyProducts || '') : getMainInsuredInitialValues
  )
  const [formikMainInsuredGroup, saveFormikMainInsuredGroup] = useState<FormikMainInsuredGroup>(
    historyGroup ? historyGroup : getGroupInitialValues
  )
  const validationSchemas = useValidationSchemasGroup()

  const formik = useFormik<FormikMainInsuredGroup>({
    initialValues: formikMainInsuredGroup,
    validationSchema: validationSchemas[currentStep],
    enableReinitialize: true,
    onSubmit: (values, { setTouched, setSubmitting }) => {
      setCurrentStep(Math.min(3, currentStep + 1))
      dispatch(shoot.reduce_history_item('history_current_step', (Math.min(3, currentStep + 1)).toString()))
      setTouched({})

      if (currentStep + 1 === 2) {
        values.originGroup = values?.group
      }
      setValues(values)
      saveFormikMainInsuredGroup(values)
      dispatch(shoot.reduce_history_item('history_group', values))

      if (currentStep + 1 === 3) {
        const tempGroup = values?.group || []
        if (tempGroup && values?.mainInsured?.isInsured === 'id_yes') {
          const newGroup = tempGroup?.filter((item) => item?.id !== values?.mainInsured?.id)
          setValues({ ...values, group: newGroup })
          saveFormikMainInsuredGroup({ ...values, group: newGroup })
          dispatch(shoot.reduce_history_item('history_group', { ...values, group: newGroup }))
        }
      }

      if (currentStep + 1 === 4) {
        doPostPayment()
      }

      setSubmitting(false)
      // setPaymentData(undefined)
      // setPaymentResponse(undefined)
    },
  })

  const doPostPayment = () => {
    submit_captcha().then((token) => {
      dispatch(
        shoot.saga_payment({
          agencyId: agency?.id,
          quoteId: quote?.quoteId,
          token: token,
          language: language.toUpperCase(),
        })
      )
    })
  }

  const { values, setValues } = formik
  const { triggerFormikCheck } = useScrollToErrorGroup(formik)

  useEffect(() => {
    if (values && values.group && values.group.length > 0) {
      const hasErrors = values.group.some(
        (asset) => asset.errors && Object.keys(asset.errors).length > 0
      )
      setIsDisabled(hasErrors)
    } else {
      setIsDisabled(true)
    }

    if (values && values.mainInsured && values.mainInsured.discount === '') {
      setDiscountError('')
    }
  }, [values])

  const evaluatePremium = () => {
    setUpdatePremium(true)
  }

  useEffect(() => {
    if (updatePremium) {
      if (currentStep === 1) {
        setScrollEvaluated(true)
      }
      let insuredDays_ = ''
      if (formikMainInsured.insured_days !== 'daily') {
        insuredDays_ = daysOptions?.find((item) => item?.id === formikMainInsured.insured_days)?.id?.split('_')[1] || ''
      } else {
        insuredDays_ = dailyOptions?.find((item) => item?.id === formikMainInsured.insured_daily)?.id?.split('_')[1] || ''
      }
      const body_ = {
        coverage: formikMainInsured.coverage_type_radios,
        insuredNumber: formik?.values?.group.length,
        insuredDays: insuredDays_,
        isFamily: false
      }
      dispatch(
        shoot.saga_evaluate_premium(body_)
      )
      setUpdatePremium(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updatePremium,
    currentStep,
    values
  ])

  return {
    t,
    currentStep,
    formik,
    agency,
    evaluatedPremium,
    isGroup,
    quote,
    loading: loadingPayment || loadingPremium,
    documents: historyDocuments,
    language,
    insuredLimit,
    isDisabled,
    discountError,
    formikMainInsured,
    validationSchemas,
    setDiscountError,
    goBack,
    evaluatePremium,
    triggerFormikCheck
  }
}
